import Highlight from '../components/Highlight';
import Heading from '../components/Heading';

const GettingStarted = () => {
  return (
    <div>
      <p>
        <code>state</code> is a single reactive object that holds your whole application state. You can't modify the
        state directly.
      </p>
      <Highlight language="javascript">
        {indexCode}
      </Highlight>

      <hr />

      <p>
        State is modified by committing <code>mutations</code> and the value is retrieved by using <code>getters</code>.
      </p>
      <Highlight language="javascript">
        {indexCode2}
      </Highlight>

      <hr />

      <Heading id="modules">modules</Heading>
      <p>
        Modules allow cleaner store structure by giving you the option to organize specific logic in smaller chunks. Access modules with hooks by using their module name prefix, for example <code>useGetter('project/projects')</code> or <code>useMutation('project/PROJECTS_SET')</code>.
      </p>
      <p>
          A module's structure is the same as the default store structure - it can contain <code>state</code>, <code>getters</code>, <code>mutations</code> and <code>actions</code>.
      </p>
      <p>
        When working with getters, mutations and actions, the <code>state</code> and the <code>context</code> parameters are scoped to the current module. That means a module's getter can't see its parent state - and a module's action can't dispatch a parent's action, or commit a parent's mutation.
      </p>
      <Highlight language="javascript" code={modulesCode} />

      <hr />

      <Heading id="withStore">withStore</Heading>
      <h3 className="heading-types">
        withStore(Component: ReactNode, store: VuexStoreType) => (props: any) => JSX.Element;
      </h3>
      <p>
        Export your <code>store</code> config file and use the <code>withStore()</code> higher order component to wrap
        your root Component with it. <code>withStore()</code> takes care of creating multiple contexts which will be
        used by the package-provided hooks.
      </p>
      <p className="text-danger">
        Your implementation won't work if you don't wrap your root Component with the store config.
      </p>
      <Highlight language="javascript" code={withStore} />
    </div>
  )
}

const indexCode = `const store = {
  state: {
    posts: [],
    favoriteIds: []
  }
}`

const withStore = `import React from 'react';
import ReactDOM from 'react-dom';
import { withStore } from 'vuex-but-for-react';

import App from './App';
import store from './store';

const AppWithStore = withStore(App, store);

ReactDOM.render(
  <AppWithStore />,
  document.getElementById('root')
);`

const indexCode2 = `const store = {
  state: {
    ...
  },
  mutations: {
    POSTS_SET (state, data) {
      state.posts = data
    },
    FAVOURITE_ADD (state, id) {
      state.favoriteIds.push(id)
    },
    FAVOURITE_REMOVE (state, idToRemove) {
      state.favoriteIds = state.favoriteIds.filter(id => id !== idToRemove)
    }
  },
  getters: {
    posts (state) {
      return state.posts
    },
    favoriteIds (state) {
      return state.favoriteIds
    }
  }
}
`

const modulesCode = `const store = {
  state: {
    ...
  },
  mutations: {
    ...
  },
  getters: {
    ...
  },
  modules: {
    project: {
      state: {
        data: [{ id: 1, title: 'Dummy project' }]
      },
      mutations: {
        PROJECTS_SET (state, data) {
          state.data = data
        }
      },
      actions: {
        async PROJECTS_FETCH (context) {
          const data = await dummyApiCall()
          context.mutations.PROJECTS_SET(data)
        }
      },
      getters: {
        projects (state) {
          return state.data
        }
      }
    }
  }
}
`

export default GettingStarted;
